import React, { useEffect, useState } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel, MenuItem, Select } from '@mui/material';

import { getCatalog, type NetworkCatalogFeed } from '../../services/network/networkApi';

/**
 * Catalog-backed network picker used wherever a default transit network is chosen
 * (user profile, company settings). Lists the entitlement-filtered feed catalog plus
 * a "none" option; a saved-but-now-missing feed is still shown so the value isn't lost.
 */
interface Props {
  value: string | null;
  onChange: (feedId: string | null) => void;
  label: string;
  /** Label for the "no default" option. */
  noneLabel: string;
  helperText?: string;
  disabled?: boolean;
}

const NONE_VALUE = '__none__';

export const NetworkFeedSelect: React.FC<Props> = ({ value, onChange, label, noneLabel, helperText, disabled }) => {
  const [feeds, setFeeds] = useState<NetworkCatalogFeed[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const ctl = new AbortController();
    setLoading(true);
    setError(false);
    getCatalog(ctl.signal)
      .then(setFeeds)
      .catch(() => {
        if (!ctl.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!ctl.signal.aborted) setLoading(false);
      });
    return () => ctl.abort();
  }, []);

  const selectValue = value ?? NONE_VALUE;
  // Keep a saved feed that's no longer in the catalog (removed / lost entitlement) visible.
  const valueMissing = value != null && !feeds.some((f) => f.feedId === value);
  const labelId = `network-feed-select-${label.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <FormControl fullWidth size="small" disabled={disabled || loading} error={error}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        label={label}
        value={selectValue}
        onChange={(e) => {
          const v = String(e.target.value);
          onChange(v === NONE_VALUE ? null : v);
        }}
        IconComponent={loading ? () => <CircularProgress size={16} sx={{ mr: 1.5 }} /> : undefined}
      >
        <MenuItem value={NONE_VALUE}>{noneLabel}</MenuItem>
        {valueMissing && <MenuItem value={value as string}>{value}</MenuItem>}
        {feeds.map((f) => (
          <MenuItem key={f.feedId} value={f.feedId}>
            {f.onestopId || f.feedId}
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};

export default NetworkFeedSelect;
